import React, { useEffect, useMemo, useRef, useState } from "react";
import { NumberRangeStatus } from "../shared/NumberRangeStatus";
import { useRangeStatusApi } from "../hooks/rangeStatusService";

import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    FormControlLabel,
    Stack,
    Switch,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    LinearProgress,
    Paper,
    CircularProgress,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PieChartIcon from "@mui/icons-material/PieChart";
import TableRowsIcon from "@mui/icons-material/TableRows";
import { statusDotColors } from "../theme/appTheme";

import {
    Tooltip,
    PieChart,
    Pie,
    Sector
} from "recharts";

import {
    NumberRangeBaseFunction,
    NumberRangeSubFunction,
    NumberRangeDescription
} from "../shared/NumberRange";

import { config } from "../config";
import { useEventsApi } from "../hooks/useEventsApi";
import { useVehicleSoftwareEventScanApi } from "../hooks/useVehicleSoftwareEventScanApi";
import type {
    VehicleSoftwareScanBasis,
    VehicleSoftwareScanCoverageGapByRangeRow,
} from "../shared/vehicleSoftwareEventScan";

const FLAT_ACCORDION_SX = {
    border: "none",
    boxShadow: "none",
    "&::before": { display: "none" },
    "&.Mui-expanded": { margin: 0 },
} as const;

/* ============================================================
   Farben für SubSlices
   ============================================================ */
const COLORS = ["#1976d2", "#9c27b0", "#ff9800", "#4caf50", "#f44336", "#00acc1"];

/* ============================================================
   Tooltip für PieChart (mit echten Namen + Prozent)
   ============================================================ */
const PieTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;

    const p = payload[0];
    const entry = p.payload;
    const total = entry.__total ?? 0;
    const percent = total > 0 ? (entry.value / total) * 100 : 0;

    return (
        <Paper elevation={3} style={{ padding: "8px" }}>
            <Typography variant="body2"><b>{entry.name}</b></Typography>
            <Typography variant="body2">Value: {entry.value}</Typography>
            <Typography variant="body2">Proportion: {percent.toFixed(1)}%</Typography>
        </Paper>
    );
};

function normalizeList<T>(data: unknown): T[] {
    if (Array.isArray(data)) {
        return data as T[];
    }
    if (
        data &&
        typeof data === "object" &&
        Array.isArray((data as { results?: unknown }).results)
    ) {
        return (data as { results: T[] }).results;
    }
    return [];
}

/* ============================================================
   Hauptkomponente
   ============================================================ */

type AdminNumberRangeStatusPageProps = {
    /** Increment when number-range labels are saved so names and charts stay in sync. */
    labelsDataVersion?: number;
};

const AdminNumberRangeStatusPage: React.FC<AdminNumberRangeStatusPageProps> = ({
    labelsDataVersion = 0,
}) => {

    const theme = useTheme();
    const { callApi } = useEventsApi();
    const callApiRef = useRef(callApi);
    callApiRef.current = callApi;

    const { fetchRangeStatus } = useRangeStatusApi();
    const fetchRangeStatusRef = useRef(fetchRangeStatus);
    fetchRangeStatusRef.current = fetchRangeStatus;

    const { listCoverageGapsByNumberRange } = useVehicleSoftwareEventScanApi();
    const listCoverageGapsByNumberRangeRef = useRef(listCoverageGapsByNumberRange);
    listCoverageGapsByNumberRangeRef.current = listCoverageGapsByNumberRange;

    const [data, setData] = useState<NumberRangeStatus[]>([]);
    const [loading, setLoading] = useState(true);

    const [baseFunctions, setBaseFunctions] = useState<NumberRangeBaseFunction[]>([]);
    const [loadingBase, setLoadingBase] = useState(true);

    const [subFunctionMap, setSubFunctionMap] = useState<Record<number, NumberRangeSubFunction[]>>({});
    const [loadedBaseIds, setLoadedBaseIds] = useState<Set<number>>(new Set());

    const [gapScanBasis, setGapScanBasis] = useState<VehicleSoftwareScanBasis>("latest_per_software");
    const [gapExcludeResolving, setGapExcludeResolving] = useState(false);
    /** Match CSV EventCode against numeric legacy code when set (same as Admin vehicle software scans) */
    const [gapUseLegacy, setGapUseLegacy] = useState(false);
    const [gapBySubId, setGapBySubId] = useState<Map<number, VehicleSoftwareScanCoverageGapByRangeRow>>(new Map());
    const [gapLoading, setGapLoading] = useState(true);
    const [gapFetchError, setGapFetchError] = useState(false);
    /** False when there are no EventCode rows in the vehicle software scan archive — comparison is N/A */
    const [gapVehicleScanRowsExist, setGapVehicleScanRowsExist] = useState<boolean | null>(null);


    /* ============================================================
       Base functions + status (initial and after label edits)
       ============================================================ */
    useEffect(() => {
        let cancelled = false;

        (async () => {
            setLoading(true);
            setLoadingBase(true);
            setLoadedBaseIds(new Set());
            setSubFunctionMap({});

            const basePath = config.apiUrl.replace(/\/+$/, "");

            try {
                const url = `${basePath}/api/number-range-base-function/?page_size=all`;
                const res = await callApiRef.current(url, "GET");
                if (!cancelled) {
                    setBaseFunctions(normalizeList<NumberRangeBaseFunction>(res));
                }
            } catch (error) {
                console.error("Failed to load base functions:", error);
                if (!cancelled) {
                    setBaseFunctions([]);
                }
            } finally {
                if (!cancelled) {
                    setLoadingBase(false);
                }
            }

            if (cancelled) {
                return;
            }

            try {
                const result = await fetchRangeStatusRef.current();
                if (!cancelled) {
                    setData(Array.isArray(result) ? result : []);
                }
            } catch (err) {
                console.error("Failed to load number range status:", err);
                if (!cancelled) {
                    setData([]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [labelsDataVersion]);


    /* ============================================================
       SubFunctions 1x pro BaseDigit
       ============================================================ */
    const fetchSubFunctions = async (baseDigit: number) => {
        try {
            if (loadedBaseIds.has(baseDigit)) return;

            const basePath = config.apiUrl.replace(/\/+$/, "");
            const url = `${basePath}/api/number-range-sub-function/?base=${baseDigit}&page_size=all`;
            const res = await callApiRef.current(url, "GET");
            const subs = normalizeList<NumberRangeSubFunction>(res);

            setSubFunctionMap(prev => ({
                ...prev,
                [baseDigit]: subs,
            }));

            setLoadedBaseIds(prev => new Set(prev).add(baseDigit));

        } catch (error) {
            console.error("Failed to load sub functions:", error);
        }
    };


    /* ============================================================
       SubFunctions nachladen, wenn data vorhanden
       ============================================================ */
    useEffect(() => {
        if (!data.length) return;

        const baseDigits = Array.from(new Set(data.map(d => d.base_digit)));
        baseDigits.forEach(digit => {
            if (!loadedBaseIds.has(digit)) {
                fetchSubFunctions(digit);
            }
        });

    }, [data, loadedBaseIds]);


    /* Vehicle software scan coverage vs catalog — same rules as admin vehicle scan gaps */
    useEffect(() => {
        let cancelled = false;
        setGapLoading(true);
        setGapFetchError(false);
        void (async () => {
            try {
                const raw = await listCoverageGapsByNumberRangeRef.current({
                    useLegacyEventCode: gapUseLegacy,
                    scanBasis: gapScanBasis,
                    excludeResolvingEvents: gapExcludeResolving || undefined,
                });
                if (cancelled) return;
                const list = (raw?.results ?? []) as VehicleSoftwareScanCoverageGapByRangeRow[];
                const m = new Map<number, VehicleSoftwareScanCoverageGapByRangeRow>();
                for (const r of list) {
                    m.set(r.sub_id, r);
                }
                setGapBySubId(m);
                if (raw != null && typeof raw === "object" && "vehicle_scan_rows_exist" in raw) {
                    setGapVehicleScanRowsExist(
                        Boolean((raw as { vehicle_scan_rows_exist?: boolean }).vehicle_scan_rows_exist)
                    );
                } else {
                    setGapVehicleScanRowsExist(true);
                }
            } catch {
                if (!cancelled) {
                    setGapBySubId(new Map());
                    setGapFetchError(true);
                    setGapVehicleScanRowsExist(null);
                }
            } finally {
                if (!cancelled) {
                    setGapLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [labelsDataVersion, gapScanBasis, gapExcludeResolving, gapUseLegacy]);


    /* ============================================================
       Labels
       ============================================================ */
    const getBaseLabel = (digit: number) => {
        const bf = baseFunctions.find(b => b.digit === digit);
        if (!bf) return "...";

        const desc = bf.descriptions?.find(d => d.language_code === "eng");
        return desc?.display_name ?? `Base ${bf.digit}`;
    };

    const getSubLabel = (sf: NumberRangeSubFunction) => {
        const desc = sf.descriptions?.find((d: NumberRangeDescription) => d.language_code === "eng");
        return desc?.display_name ?? `Sub ${sf.digit}`;
    };


    /* ============================================================
       PieChart Data per Base generieren (mit echten Texten)
       ============================================================ */
    const basePieDataArray = useMemo(() => {

        const grouped: Record<number, {
            base_digit: number;
            total_capacity: number;
            total_used: number;
            sections: { name: string; value: number; color: string; key: string }[];
        }> = {};

        for (const row of data) {
            const baseDigit = row.base_digit;

            if (!grouped[baseDigit]) {
                grouped[baseDigit] = {
                    base_digit: baseDigit,
                    total_capacity: 0,
                    total_used: 0,
                    sections: []
                };
            }

            grouped[baseDigit].total_capacity += row.capacity;
            grouped[baseDigit].total_used += row.used;

            const subList = subFunctionMap[baseDigit] || [];
            const subObj = subList.find(sf => sf.digit === row.sub_digit);
            const subName = subObj ? getSubLabel(subObj) : `Sub ${row.sub_digit}`;

            const color = COLORS[grouped[baseDigit].sections.length % COLORS.length];

            grouped[baseDigit].sections.push({
                name: subName,
                value: row.used,
                color,
                key: `b${baseDigit}-s${row.sub_digit}`
            });
        }

        return Object.values(grouped).map(base => {
            const free = base.total_capacity - base.total_used;

            return {
                base_digit: base.base_digit,
                base_label: getBaseLabel(base.base_digit),
                total_capacity: base.total_capacity,
                sections: [
                    ...base.sections.map(s => ({ ...s, __total: base.total_capacity })),
                    {
                        name: "Free",
                        value: free,
                        color: statusDotColors.neutral,
                        key: `b${base.base_digit}-free`,
                        __total: base.total_capacity
                    }
                ]
            };
        });

    }, [data, subFunctionMap, baseFunctions]);


    /* ============================================================
       Loading Screen
       ============================================================ */
    if (loading || loadingBase) {
        return (
            <Box display="flex" justifyContent="center" mt={10}>
                <CircularProgress />
            </Box>
        );
    }

    const CustomPieSlice = (props: any) => {
        const {
            cx, cy,
            innerRadius, outerRadius,
            startAngle, endAngle,
            payload
        } = props;

        const isFree = payload.name === "Free";
        const color = isFree ? statusDotColors.neutral : payload.color;

        return (
            <Sector
                cx={cx}
                cy={cy}
                innerRadius={innerRadius}
                outerRadius={outerRadius}
                startAngle={startAngle}
                endAngle={endAngle}
                fill={color}
                // Separator between slices must follow the surface, not be hard white,
                // or the chart grows a white grid in dark mode.
                stroke={theme.palette.background.paper}
                strokeWidth={1}
            />
        );
    };


    /* ============================================================
       Render
       ============================================================ */
    return (
        <Box>
            {/* ============================================================
               PIE CHART PRO BASE FUNKTION
               ============================================================ */}
            <Paper elevation={0} sx={{ mb: 2, overflow: "hidden", border: "none", boxShadow: "none" }}>
                <Accordion defaultExpanded disableGutters sx={FLAT_ACCORDION_SX}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
                            <PieChartIcon color="primary" fontSize="small" aria-hidden />
                            <Typography variant="subtitle1" fontWeight={600} sx={{ color: theme.palette.text.primary }}>
                                Fill level per base function
                            </Typography>
                            <Chip size="small" label={`${basePieDataArray.length} charts`} />
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 2, pb: 2 }}>
                        <Box display="flex" flexWrap="wrap" gap={4} justifyContent="center">
                            {basePieDataArray.map((baseItem) => (
                                <Box key={`base-pie-${baseItem.base_digit}`}>
                                    <Typography variant="subtitle1" mb={1}>
                                        {baseItem.base_label} ({baseItem.base_digit})
                                    </Typography>

                                    <PieChart width={200} height={200}>
                                        <Pie
                                            data={baseItem.sections}
                                            dataKey="value"
                                            nameKey="name"
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={100}
                                            shape={<CustomPieSlice />}
                                        />
                                        <Tooltip content={<PieTooltip />} />
                                    </PieChart>
                                </Box>
                            ))}
                        </Box>
                    </AccordionDetails>
                </Accordion>
            </Paper>



            {/* ============================================================
               BAR CHART (Subbereiche)
               ============================================================ */}
            {/*<Paper elevation={3} sx={{ p: 2, mb: 4 }}>
                <Typography sx={{ color: theme.palette.text.primary }} variant="h6" mb={2}>
                    Overview – Percentage fill level per sub-area
                </Typography>

                <Box height={300}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="range_start" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="percent_filled" fill="#1976d2" />
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            </Paper>*/}



            {/* ============================================================
               TABELLE DER BEREICHE
               ============================================================ */}
            <Paper elevation={0} sx={{ overflow: "hidden", border: "none", boxShadow: "none" }}>
                <Accordion defaultExpanded={false} disableGutters sx={FLAT_ACCORDION_SX}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap", pr: 1 }}>
                            <TableRowsIcon color="primary" fontSize="small" aria-hidden />
                            <Typography variant="subtitle1" fontWeight={600} sx={{ color: theme.palette.text.primary }}>
                                Details for each sub function
                            </Typography>
                            <Chip size="small" label={`${data.length} rows`} />
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0, pt: 0 }}>
                        <Typography sx={{ color: theme.palette.text.secondary, px: 2, pb: 1 }} variant="body2">
                            Counted are UserEvents (also unapproved ones to get most critical values).
                        </Typography>
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1.5}
                            alignItems={{ sm: "center" }}
                            flexWrap="wrap"
                            useFlexGap
                            sx={{ px: 2, pb: 1.5 }}
                        >
                            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                                Vehicle scan scope
                            </Typography>
                            <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={gapScanBasis}
                                onChange={(_, v: VehicleSoftwareScanBasis | null) => {
                                    if (v != null) setGapScanBasis(v);
                                }}
                            >
                                <ToggleButton value="latest_per_software">Latest per software</ToggleButton>
                                <ToggleButton value="all_reports">All uploads</ToggleButton>
                            </ToggleButtonGroup>
                            <FormControlLabel
                                control={
                                    <Switch
                                        size="small"
                                        checked={gapUseLegacy}
                                        onChange={(_, c) => setGapUseLegacy(c)}
                                        disabled={gapLoading}
                                    />
                                }
                                label="Use legacy code for comparison"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        size="small"
                                        checked={gapExcludeResolving}
                                        onChange={(_, c) => setGapExcludeResolving(c)}
                                        disabled={gapLoading}
                                    />
                                }
                                label="Exclude resolving events"
                            />
                            {gapFetchError && (
                                <Chip
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    label="Scan statistics unavailable — check vehicle software scan API role"
                                />
                            )}
                            {!gapLoading && !gapFetchError && gapVehicleScanRowsExist === false && (
                                <Chip
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    label="No vehicle software CSV scans stored — orange scan bar disabled until the first upload"
                                />
                            )}
                        </Stack>
                        <Typography sx={{ color: theme.palette.text.secondary, px: 2, pb: 1 }} variant="caption" display="block">
                            <strong>Fill level</strong> combines two bars: blue = share of the number range that is occupied
                            (catalog). Orange = share of <strong>Used</strong> codes in that sub range that never appear in
                            pipeline scans (same rules as Admin → Vehicle software event scans). Orange is{" "}
                            <strong>not_reported ÷ Used</strong>, not free capacity. Rows stay grouped by <strong>new</strong>{" "}
                            <code>event_code</code> bands (same as Used). With <strong>Use legacy code for comparison</strong>, CSV{" "}
                            <code>EventCode</code> values are matched against numeric legacy code (<code>event_code_old</code>) when
                            present, otherwise the current <code>event_code</code> — so an event with code 1001 and legacy 45 counts in
                            the 1001 band; scans must contain <strong>45</strong> for it to count as seen, but the row still refers to
                            the new-system slot.
                        </Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Base function</TableCell>
                                    <TableCell>Sub function</TableCell>
                                    <TableCell>Range</TableCell>
                                    <TableCell align="right">Used</TableCell>
                                    <TableCell align="right">Free</TableCell>
                                    <TableCell sx={{ minWidth: 200 }}>
                                        <Box component="span" sx={{ display: "inline-block", lineHeight: 1.25 }}>
                                            Fill level
                                            <Typography
                                                component="span"
                                                variant="caption"
                                                display="block"
                                                color="text.secondary"
                                                sx={{ fontWeight: 400 }}
                                            >
                                                Blue: range occupancy · Orange: used codes missing from scans
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {data.map((row) => {
                                    const subList = subFunctionMap[row.base_digit];
                                    const subObj = subList?.find(sf => sf.digit === row.sub_digit);
                                    const gapRow = gapBySubId.get(row.sub_id);
                                    const gapAbs = gapRow?.not_reported_in_scan ?? null;
                                    const gapPct = gapRow?.not_reported_percent_of_used ?? null;
                                    const gapBarValue =
                                        gapPct != null ? Math.min(100, Math.max(0, gapPct)) : 0;

                                    return (
                                        <TableRow key={row.sub_id} hover>
                                            <TableCell>{getBaseLabel(row.base_digit)} ({row.base_digit})</TableCell>

                                            <TableCell>
                                                <Typography sx={{ color: theme.palette.text.primary }} component="span" fontWeight={600}>
                                                    {subObj ? getSubLabel(subObj) : "…"}
                                                </Typography>
                                            </TableCell>

                                            <TableCell>
                                                {row.range_start} – {row.range_end}
                                            </TableCell>

                                            <TableCell align="right">{row.used}</TableCell>
                                            <TableCell align="right">{row.free}</TableCell>

                                            <TableCell sx={{ minWidth: 200 }}>
                                                <Stack spacing={0.75}>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={Math.min(100, Math.max(0, row.percent_filled))}
                                                            color="primary"
                                                            sx={{
                                                                flex: 1,
                                                                minWidth: 72,
                                                                maxWidth: 140,
                                                                height: 8,
                                                                borderRadius: 4,
                                                            }}
                                                        />
                                                        <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                                                            {row.percent_filled.toFixed(1)}%
                                                        </Typography>
                                                    </Box>
                                                    {gapFetchError ? (
                                                        <Typography variant="caption" color="text.disabled">
                                                            Scan bar unavailable
                                                        </Typography>
                                                    ) : gapLoading ? (
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            <CircularProgress size={14} />
                                                            <Typography variant="caption" color="text.secondary">
                                                                Loading scan coverage…
                                                            </Typography>
                                                        </Box>
                                                    ) : !gapVehicleScanRowsExist ? (
                                                        <Typography variant="caption" color="text.disabled">
                                                            No CSV scans — scan coverage bar hidden
                                                        </Typography>
                                                    ) : row.used > 0 && gapAbs != null && gapPct != null ? (
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={gapBarValue}
                                                                color="warning"
                                                                sx={{
                                                                    flex: 1,
                                                                    minWidth: 72,
                                                                    maxWidth: 140,
                                                                    height: 8,
                                                                    borderRadius: 4,
                                                                }}
                                                            />
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    whiteSpace: "nowrap",
                                                                    color: gapAbs > 0 ? "warning.main" : "text.secondary",
                                                                    fontWeight: gapAbs > 0 ? 600 : 400,
                                                                }}
                                                            >
                                                                {gapAbs}/{row.used} not in scan ({gapPct.toFixed(1)}%)
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="caption" color="text.secondary">
                                                            No used codes — scan coverage N/A
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </AccordionDetails>
                </Accordion>
            </Paper>

        </Box>
    );
};

export default AdminNumberRangeStatusPage;